import { Box, CircularProgress, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { queryClient } from "../..";
import { OverlayLoader } from "../../ui/atoms/OverlayLoader";
import { EmptyData } from "../../ui/atoms/EmptyData";
import { BillingAPI } from "../../utils/api/requests/billing-requests";
import { ReplenishmentItem } from "./ui/molecules/ReplenishmentItem";

const limit = 25;
const scrollIdent = 200;

export const ReplenishmentsList: FC = () => {
  const { t } = useTranslation();
  const [scrollLimit, setScrollLimit] = useState(limit);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const { data, refetch } = useQuery({
    queryFn: () =>
      BillingAPI.getReplenishmentsList({ search: "", limit: scrollLimit }),
    onSuccess: () => {
      setLoading(false);
      setDataLoading(false);
    },
    queryKey: "replenishmentsList",
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (scrollLimit !== limit) {
      if (data && data?.count > data?.items.length) {
        setLoading(true);
        refetch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, scrollLimit]);

  const gridTemplateColumns = "0.1fr 0.25fr 0.25fr 0.2fr 0.2fr 0.2fr";

  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = document.documentElement.scrollTop;
    if (windowHeight + scrollTop + scrollIdent >= documentHeight && !loading) {
      setScrollLimit((oldLimit) => oldLimit + limit);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    return () => {
      queryClient.resetQueries("postOrders");
    };
  }, []);

  return (
    <Box
      sx={{ width: "100%", minWidth: { xs: "none", sm: "none", md: "1024px" } }}
    >
      <Typography sx={{ mb: "16px" }} variant="h4">
        {t("replenishments.replenishments")}
      </Typography>
      {(!data || dataLoading) && <OverlayLoader />}
      <Box
        sx={{
          width: "100%",
          boxShadow:
            "rgba(0, 0, 0, 0.04) 0px 5px 22px, rgba(0, 0, 0, 0.03) 0px 0px 0px 0.5px",
          borderRadius: "16px",
        }}
      >
        {data?.items?.length ? (
          <Box
            sx={{
              backgroundColor: "rgb(248, 249, 250)",
              height: "40px",
              display: { xs: "none", sm: "none", md: "grid" },
              gridTemplateColumns: gridTemplateColumns,
              px: "24px",
              py: "8px",
              color: "rgb(47, 55, 70)",
              textTransform: "uppercase",
              borderRadius: "16px 16px 0 0",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", maxWidth: "250px" }}
            >
              <Typography sx={{ fontSize: "0.875rem" }}>{"ID"}</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {t("replenishments.date")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {t("replenishments.type")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {t("replenishments.amount")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {t("replenishments.status")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {t("replenishments.link")}
              </Typography>
            </Box>
          </Box>
        ) : null}
        {data?.items.map((replenishment) => (
          <ReplenishmentItem
            replenishment={replenishment}
            key={replenishment.id}
            gridTemplateColumns={gridTemplateColumns}
          />
        ))}
      </Box>
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: "20px",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {!data?.items.length && (
        <EmptyData text={t("replenishments.emptyData")} />
      )}
    </Box>
  );
};
